import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import { BlogPost, BlogMeta } from '@/services/blogService';
import { Button } from '@/components/ui/button';

interface BlogFooterProps {
  post: BlogPost;
  relatedPosts: BlogMeta[];
  config: {
    showRelatedPosts: boolean;
  };
}

export const BlogFooter: React.FC<BlogFooterProps> = ({ post, relatedPosts, config }) => {
  if (!config.showRelatedPosts || relatedPosts.length === 0) {
    return null;
  }

  return (
    <footer className="mt-24 pt-16 border-t border-white/10">
      {/* Related Posts Section */}
      <section>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
              Continue Reading
            </span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Explore more insights and discover related topics that might interest you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {relatedPosts.map((relatedPost, index) => (
            <Link
              key={relatedPost.slug}
              to={`/blog/${relatedPost.slug}`}
              className="group"
            >
              <article 
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:border-blue-400/50 h-full animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Image */}
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={relatedPost.image} 
                    alt={relatedPost.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-gradient-to-r from-blue-600/90 to-purple-600/90 text-white rounded-full text-xs font-medium">
                      {relatedPost.category}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  
                  <p className="text-white/70 mb-4 leading-relaxed line-clamp-3">
                    {relatedPost.excerpt}
                  </p>
                  
                  {/* Metadata */}
                  <div className="flex items-center justify-between text-white/60 text-sm mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{relatedPost.readTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(relatedPost.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  {/* Read More Button */}
                  <Button 
                    variant="ghost"
                    size="sm"
                    className="text-blue-300 hover:text-white hover:bg-blue-600/20 group w-full justify-between"
                  >
                    Read Article
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="mt-16 pt-16 border-t border-white/10">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Want to stay updated?
          </h3>
          <p className="text-white/70 mb-6 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest insights, tutorials, and industry trends.
          </p>
          <Link to="/blog">
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105">
              View All Articles
            </Button>
          </Link>
        </div>
      </section>
    </footer>
  );
};

export default BlogFooter;